import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Calendar, Pill, ExternalLink, Clock, Stethoscope } from 'lucide-react'
import type { PendingDose, DoseStatus } from '@rondela/shared'
import type { PetWithActions } from '../lib/urgency'
import { createUnifiedTimeline } from '../lib/timeline'
import {
  Avatar,
  Badge,
  AlertBanner,
  StatGrid,
  TimelineList,
  QuickActions,
  type TimelineItem as WidgetTimelineItem,
  type StatGridItem,
} from '../../../shared/components/widgets'
import { MissedDoseModal } from './MissedDoseModal'
import { RecentDoseActivity } from './RecentDoseActivity'
import { parseLocalDateTime } from '../../../shared/lib/date-utils'

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'hoy'
  if (diffDays === 1) return 'mañana'
  if (diffDays < 0) return `hace ${Math.abs(diffDays)} días`
  return `en ${diffDays} días`
}

interface PetDetailPanelProps {
  petWithActions: PetWithActions
  onDoseAction: (doseId: string, status: DoseStatus, notes?: string) => Promise<void>
  onTakeAllDoses?: (petId: string) => Promise<number>
}

function getSpeciesEmoji(especie: string): string {
  return especie === 'perro' ? '🐕' : especie === 'gato' ? '🐈' : '🐾'
}

function formatDoseTime(scheduledFor: string): string {
  return parseLocalDateTime(scheduledFor).toLocaleTimeString('es', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function findNextUpcomingDose(doses: PendingDose[]): PendingDose | undefined {
  const now = new Date()
  const pendingDoses = doses.filter(d => d.status === 'pending')

  // Sort by scheduled time
  const sorted = [...pendingDoses].sort(
    (a, b) => parseLocalDateTime(a.scheduledFor).getTime() - parseLocalDateTime(b.scheduledFor).getTime()
  )

  // Find first upcoming dose
  const upcoming = sorted.find(d => parseLocalDateTime(d.scheduledFor) >= now)
  if (upcoming) return upcoming

  // All overdue - return the most recent one (closest to now)
  return sorted[sorted.length - 1]
}

function getDoseStatus(dose: PendingDose): 'pending' | 'completed' | 'missed' {
  const now = new Date()
  const scheduled = parseLocalDateTime(dose.scheduledFor)

  if (dose.status === 'taken') return 'completed'
  if (dose.status === 'missed' || dose.status === 'skipped') return 'missed'
  if (scheduled < now) return 'missed'
  return 'pending'
}

export function PetDetailPanel({ petWithActions, onDoseAction, onTakeAllDoses }: PetDetailPanelProps) {
  const { pet, pendingDoses, recentDoses, upcomingAppointments, alerts } = petWithActions

  const [missedDose, setMissedDose] = useState<PendingDose | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isTakingAll, setIsTakingAll] = useState(false)

  // Create unified timeline
  const timelineItems = useMemo(
    () => createUnifiedTimeline(pendingDoses, upcomingAppointments),
    [pendingDoses, upcomingAppointments]
  )

  // Convert to widget timeline format
  const widgetTimelineItems: WidgetTimelineItem[] = useMemo(() => {
    return timelineItems.map((item) => {
      if (item.type === 'dose') {
        const dose = item.data
        return {
          id: dose.id,
          time: formatDoseTime(dose.scheduledFor),
          content: (
            <span className="pet-detail__dose-content">
              <Pill size={14} />
              <strong>{dose.medicationNombre}</strong> {dose.dosis}
            </span>
          ),
          status: getDoseStatus(dose),
          actions:
            getDoseStatus(dose) !== 'completed'
              ? [
                  {
                    label: 'Tomar',
                    onClick: () => handleTaken(dose),
                    variant: 'primary' as const,
                    loading: isLoading,
                  },
                  {
                    label: 'No tomada',
                    onClick: () => setMissedDose(dose),
                    variant: 'secondary' as const,
                  },
                ]
              : undefined,
        }
      } else {
        const appointment = item.data
        return {
          id: appointment.id,
          time: appointment.hora || '09:00',
          content: (
            <span className="pet-detail__appointment-content">
              <Calendar size={14} />
              <strong>{appointment.tipo}</strong> - {appointment.motivo}
            </span>
          ),
          status: 'pending' as const,
        }
      }
    })
  }, [timelineItems, isLoading])

  // Calculate period counts for stat grid
  const periodCounts: StatGridItem[] = useMemo(() => {
    const counts = { morning: 0, afternoon: 0, evening: 0 }
    const completed = { morning: 0, afternoon: 0, evening: 0 }

    for (const dose of pendingDoses) {
      const hour = parseLocalDateTime(dose.scheduledFor).getHours()
      if (hour < 12) {
        counts.morning++
        if (dose.status === 'taken') completed.morning++
      } else if (hour < 18) {
        counts.afternoon++
        if (dose.status === 'taken') completed.afternoon++
      } else {
        counts.evening++
        if (dose.status === 'taken') completed.evening++
      }
    }

    return [
      {
        label: '☀️ Mañana',
        value: `${completed.morning}/${counts.morning}`,
        status: completed.morning === counts.morning && counts.morning > 0 ? 'success' : undefined,
      },
      {
        label: '🌅 Tarde',
        value: `${completed.afternoon}/${counts.afternoon}`,
        status: completed.afternoon === counts.afternoon && counts.afternoon > 0 ? 'success' : undefined,
      },
      {
        label: '🌙 Noche',
        value: `${completed.evening}/${counts.evening}`,
        status: completed.evening === counts.evening && counts.evening > 0 ? 'success' : undefined,
      },
    ]
  }, [pendingDoses])

  const handleTaken = async (dose: PendingDose) => {
    setIsLoading(true)
    try {
      await onDoseAction(dose.id, 'taken')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMissedConfirm = async (instructions: string) => {
    if (!missedDose) return
    setIsLoading(true)
    try {
      await onDoseAction(missedDose.id, 'skipped', instructions)
      setMissedDose(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleTakeAll = async () => {
    setIsTakingAll(true)
    try {
      if (onTakeAllDoses) {
        // Use batch endpoint if available
        await onTakeAllDoses(pet.id)
      } else {
        // Fallback: iterate through each dose
        const pendingNow = pendingDoses.filter((d) => {
          const scheduled = parseLocalDateTime(d.scheduledFor)
          return scheduled <= new Date() && d.status === 'pending'
        })
        for (const dose of pendingNow) {
          await onDoseAction(dose.id, 'taken')
        }
      }
    } finally {
      setIsTakingAll(false)
    }
  }

  const pendingNowCount = pendingDoses.filter((d) => {
    const scheduled = parseLocalDateTime(d.scheduledFor)
    return scheduled <= new Date() && d.status === 'pending'
  }).length

  // Count overdue doses (past scheduled time but still pending)
  const overdueDosesCount = pendingDoses.filter((d) => {
    const scheduled = parseLocalDateTime(d.scheduledFor)
    return scheduled < new Date() && d.status === 'pending'
  }).length

  // Count missed doses (marked as missed or skipped)
  const missedDosesCount = pendingDoses.filter(
    (d) => d.status === 'missed' || d.status === 'skipped'
  ).length

  const hasAlerts = alerts.length > 0
  const hasMissedDosesWarning = overdueDosesCount > 0 || missedDosesCount > 0

  // Get next dose info for status indicator
  const nextPendingDose = findNextUpcomingDose(pendingDoses)
  const nextAppointment = upcomingAppointments[0]

  return (
    <>
      <div className="pet-detail-panel">
        {/* Header */}
        <div className="pet-detail-panel__header">
          <Avatar
            src={pet.fotoUrl}
            fallback={getSpeciesEmoji(pet.especie)}
            size="lg"
          />
          <div className="pet-detail-panel__info">
            <h2 className="pet-detail-panel__name">{pet.nombre}</h2>
            <div className="pet-detail-panel__meta">
              {pet.peso && <Badge variant="pill">{pet.peso} kg</Badge>}
              {pet.raza && <Badge variant="pill">{pet.raza}</Badge>}
            </div>
            {/* Status indicators */}
            <div className="pet-detail-panel__status-indicators">
              {nextPendingDose && (
                <div className="pet-detail-panel__status-item">
                  <Clock size={14} />
                  <span>Próxima dosis: {nextPendingDose.medicationNombre} {formatDoseTime(nextPendingDose.scheduledFor)}</span>
                </div>
              )}
              {nextAppointment?.proximaCita && (
                <div className="pet-detail-panel__status-item">
                  <Stethoscope size={14} />
                  <span>Próximo control {formatRelativeDate(nextAppointment.proximaCita)}</span>
                </div>
              )}
            </div>
          </div>
          <Link
            to={`/mascotas/${pet.id}`}
            className="pet-detail-panel__link"
            title="Ver perfil completo"
          >
            <ExternalLink size={20} />
          </Link>
        </div>

        {/* Missed Doses Warning */}
        {hasMissedDosesWarning && (
          <AlertBanner
            variant="error"
            action={
              overdueDosesCount > 0
                ? {
                    label: 'Tomar ahora',
                    onClick: handleTakeAll,
                  }
                : undefined
            }
          >
            {overdueDosesCount > 0 && (
              <strong>
                {overdueDosesCount} {overdueDosesCount === 1 ? 'dosis atrasada' : 'dosis atrasadas'}
              </strong>
            )}
            {overdueDosesCount > 0 && missedDosesCount > 0 && ' · '}
            {missedDosesCount > 0 && (
              <span>
                {missedDosesCount} {missedDosesCount === 1 ? 'dosis no tomada' : 'dosis no tomadas'} hoy
              </span>
            )}
          </AlertBanner>
        )}

        {/* Health Alerts */}
        {hasAlerts && (
          <div className="pet-detail-panel__alerts">
            {alerts.map((alert, idx) => (
              <AlertBanner key={idx} variant="warning">
                {alert.message}
              </AlertBanner>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {pendingNowCount > 0 && (
          <div className="pet-detail-panel__quick-actions">
            <QuickActions
              actions={[
                {
                  label: `Tomar todo (${pendingNowCount})`,
                  onClick: handleTakeAll,
                  icon: CheckCircle2,
                  variant: 'primary',
                  loading: isTakingAll,
                },
              ]}
            />
          </div>
        )}

        {/* Stats */}
        {pendingDoses.length > 0 && (
          <div className="pet-detail-panel__stats">
            <h3 className="pet-detail-panel__section-title">Progreso del día</h3>
            <StatGrid items={periodCounts} columns={3} />
          </div>
        )}

        {/* Timeline */}
        <div className="pet-detail-panel__timeline">
          <h3 className="pet-detail-panel__section-title">Medicamentos de hoy</h3>
          <TimelineList
            items={widgetTimelineItems}
            groupBy="time"
            emptyMessage="Sin medicamentos pendientes para hoy"
          />
        </div>

        {/* Recent Activity */}
        {recentDoses.length > 0 && (
          <div className="pet-detail-panel__recent">
            <h3 className="pet-detail-panel__section-title">Actividad reciente</h3>
            <RecentDoseActivity doses={recentDoses} />
          </div>
        )}
      </div>

      {/* Missed Dose Modal */}
      {missedDose && (
        <MissedDoseModal
          medicationName={`${missedDose.medicationNombre} ${missedDose.dosis}`}
          petName={pet.nombre}
          onConfirm={handleMissedConfirm}
          onCancel={() => setMissedDose(null)}
          isLoading={isLoading}
        />
      )}
    </>
  )
}
